
import Vue from 'vue';
import { hasPermission } from '@/models/User';
import {
  getEmbedUrl,
  getPowerBiUrl,
  categoryOrder,
  categoryIcons,
  mapPermissionKey,
} from '@/config/powerBiReports';
import {
  powerBiConfigService,
  PowerBiReportPage,
  PowerBiReport,
  RefreshStatus,
  PowerBiRefreshNotification,
} from '@/services/PowerBiConfigService';
import * as signalR from '@microsoft/signalr';

const SETUP_DISMISSED_KEY = 'powerbi-setup-dismissed';
const AUTH_NOTICE_DISMISSED_KEY = 'powerbi-auth-notice-dismissed';

export default Vue.extend({
  name: 'PowerBiReports',

  data() {
    return {
      currentReport: null as PowerBiReport | null,
      selectedPage: null as PowerBiReportPage | null,
      loading: false,
      configLoading: true,
      configError: null as string | null,
      searchQuery: '',
      sidebarCollapsed: false,
      expandedCategories: {} as Record<string, boolean>,
      showSetupDialog: false,
      showAuthNotice: false,
      dontShowSetupAgain: false,
      frameLoadCount: 0,
      // Dataset refresh state
      datasetRefreshing: false,
      canRefreshDataset: true,
      cooldownRemainingMinutes: null as number | null,
      lastDatasetRefreshTime: null as string | null,
      // SignalR connection
      signalRConnection: null as signalR.HubConnection | null,
      // Refresh completion dialog
      showRefreshCompletedDialog: false,
      lastRefreshNotification: null as PowerBiRefreshNotification | null,
    };
  },

  computed: {
    currentUser(): any {
      return this.$store.getters.userInfo;
    },

    isAdmin(): boolean {
      return this.currentUser?.isAdmin === true;
    },

    formattedLastRefreshTime(): string {
      if (!this.lastDatasetRefreshTime) return '';
      const date = new Date(this.lastDatasetRefreshTime);
      return date.toLocaleString();
    },

    refreshDatasetTooltip(): string {
      if (this.datasetRefreshing) {
        return 'Refreshing dataset...';
      }
      if (!this.canRefreshDataset && this.cooldownRemainingMinutes) {
        return `Refresh available in ${this.cooldownRemainingMinutes} minutes`;
      }
      return 'Refresh Power BI Dataset';
    },

    // Get pages from current report
    reportPages(): PowerBiReportPage[] {
      return this.currentReport?.pages || [];
    },

    // Filter pages based on user permissions
    allowedPages(): PowerBiReportPage[] {
      return this.reportPages.filter((page) => {
        // Admin users can see ALL reports
        if (this.currentUser?.isAdmin) return true;
        // If no permission specified, allow access
        if (!page.permissionKey) return true;
        // Map permission key to enum and check
        const permission = mapPermissionKey(page.permissionKey);
        if (!permission) return true;
        return hasPermission(this.currentUser, permission);
      });
    },

    // Group allowed pages by category
    pagesByCategory(): Record<string, PowerBiReportPage[]> {
      return this.allowedPages.reduce((acc, page) => {
        if (!acc[page.category]) {
          acc[page.category] = [];
        }
        acc[page.category].push(page);
        return acc;
      }, {} as Record<string, PowerBiReportPage[]>);
    },

    // Filter by search query
    filteredPagesByCategory(): Record<string, PowerBiReportPage[]> {
      if (!this.searchQuery) {
        return this.pagesByCategory;
      }

      const query = this.searchQuery.toLowerCase();
      const filtered: Record<string, PowerBiReportPage[]> = {};

      for (const [category, pages] of Object.entries(this.pagesByCategory) as [string, PowerBiReportPage[]][]) {
        const matchingPages = pages.filter(
          (page: PowerBiReportPage) =>
            page.displayName.toLowerCase().includes(query) ||
            category.toLowerCase().includes(query)
        );
        if (matchingPages.length > 0) {
          filtered[category] = matchingPages;
        }
      }

      return filtered;
    },

    orderedCategories(): string[] {
      return categoryOrder.filter(
        (cat) => this.filteredPagesByCategory[cat]?.length > 0
      );
    },

    currentEmbedUrl(): string {
      if (!this.selectedPage || !this.currentReport) return '';
      const url = getEmbedUrl(this.currentReport, this.selectedPage);
      // If URL is empty but we have a selected page, config may have been cleared
      if (!url && !this.configLoading) {
        // Trigger async reload (can't await in computed, so trigger separately)
        this.handleStaleConfig();
      }
      return url;
    },

    // Check if all visible categories are expanded
    allCategoriesExpanded(): boolean {
      return this.orderedCategories.every((cat) => this.expandedCategories[cat]);
    },
  },

  watch: {
    // Auto-expand categories with matching results when searching
    searchQuery(newQuery: string) {
      if (newQuery) {
        // Expand all categories that have matching results
        Object.keys(this.filteredPagesByCategory).forEach((category) => {
          this.$set(this.expandedCategories, category, true);
        });
      }
      // When cleared, keep current expanded state (don't collapse)
    },
  },

  async created() {
    // Initialize all categories as collapsed by default
    categoryOrder.forEach((cat) => {
      this.$set(this.expandedCategories, cat, false);
    });

    // Check if we should show setup dialog
    const setupDismissed = localStorage.getItem(SETUP_DISMISSED_KEY);
    if (!setupDismissed) {
      this.showSetupDialog = true;
    }

    // Load Power BI configuration from backend
    await this.loadConfig();

    // Load refresh status for admin users
    if (this.isAdmin) {
      await this.loadRefreshStatus();
    }

    // Setup SignalR connection for real-time refresh notifications
    this.setupSignalRConnection();

    // Check URL for pre-selected report (only after config is loaded)
    if (!this.configError && this.currentReport) {
      const reportId = this.$route.query.report as string;
      if (reportId) {
        const page = this.allowedPages.find((p) => p.pageKey === reportId);
        if (page) {
          // Expand the category first, then select the page
          this.$set(this.expandedCategories, page.category, true);
          this.selectPage(page);
        }
      }
    }
  },

  beforeDestroy() {
    // Clean up SignalR connection
    if (this.signalRConnection) {
      this.signalRConnection.stop().catch((err) => {
        console.warn('Error stopping SignalR connection:', err);
      });
      this.signalRConnection = null;
    }
  },

  methods: {
    async loadConfig() {
      this.configLoading = true;
      this.configError = null;
      try {
        await powerBiConfigService.getConfig();
        // Use the default report (or first report)
        this.currentReport = powerBiConfigService.getDefaultReport();
        if (!this.currentReport) {
          this.configError = 'No reports configured';
        }
      } catch (error: any) {
        console.error('Failed to load Power BI configuration:', error);
        this.configError = error?.response?.data?.message || error?.message || 'Failed to load configuration';
      } finally {
        this.configLoading = false;
      }
    },

    /**
     * Handles stale config by silently reloading in the background.
     * Called when embed URL is empty but we have a selected page.
     */
    async handleStaleConfig() {
      // Prevent multiple simultaneous reload attempts
      if (this.configLoading) return;

      console.info('Power BI config stale, reloading...');
      this.configLoading = true;

      try {
        const loaded = await powerBiConfigService.ensureConfig();
        if (loaded) {
          // Refresh current report reference
          this.currentReport = powerBiConfigService.getDefaultReport();
          // Force reactivity update by touching selectedPage
          if (this.selectedPage) {
            const page = this.selectedPage;
            this.selectedPage = null;
            this.$nextTick(() => {
              this.selectedPage = page;
            });
          }
        } else {
          this.configError = 'Failed to reload configuration';
        }
      } catch (error: any) {
        console.error('Failed to reload Power BI configuration:', error);
        this.configError = error?.message || 'Failed to reload configuration';
      } finally {
        this.configLoading = false;
      }
    },

    selectPage(page: PowerBiReportPage) {
      // Don't reload if same page
      if (this.selectedPage?.pageKey === page.pageKey) return;

      this.selectedPage = page;
      this.loading = true;
      this.frameLoadCount = 0;

      // Auto-expand the category of the selected report
      if (page.category && !this.expandedCategories[page.category]) {
        this.$set(this.expandedCategories, page.category, true);
      }

      // Update URL without navigation
      if (this.$route.query.report !== page.pageKey) {
        this.$router.replace({
          query: { ...this.$route.query, report: page.pageKey },
        }).catch(() => {});
      }

      // Show auth notice on first report load if not dismissed
      const authDismissed = localStorage.getItem(AUTH_NOTICE_DISMISSED_KEY);
      if (!authDismissed) {
        this.showAuthNotice = true;
      }
    },

    toggleAllCategories() {
      const expand = !this.allCategoriesExpanded;
      this.orderedCategories.forEach((cat) => {
        this.$set(this.expandedCategories, cat, expand);
      });
    },

    toggleCategory(category: string) {
      this.$set(
        this.expandedCategories,
        category,
        !this.expandedCategories[category]
      );
    },

    getCategoryIcon(category: string): string {
      return categoryIcons[category] || 'mdi-folder-outline';
    },

    onFrameLoad() {
      this.frameLoadCount++;
      // Consider loaded after first frame load
      if (this.frameLoadCount >= 1) {
        this.loading = false;
        // Auto-dismiss auth notice after successful load
        if (this.showAuthNotice) {
          this.dismissAuthNotice();
        }
      }
    },

    async refreshReport() {
      const iframe = this.$refs.reportFrame as HTMLIFrameElement;
      if (iframe && this.selectedPage) {
        this.loading = true;
        this.frameLoadCount = 0;

        // Ensure config is loaded before refreshing
        if (!powerBiConfigService.isConfigLoaded()) {
          await this.handleStaleConfig();
        }

        // Force reload with cache-busting timestamp to ensure current data
        const url = this.currentEmbedUrl;
        if (url) {
          const separator = url.includes('?') ? '&' : '?';
          iframe.src = `${url}${separator}_t=${Date.now()}`;
        } else {
          this.loading = false;
        }
      }
    },

    async openInPowerBi() {
      if (this.selectedPage && this.currentReport) {
        // Ensure config is loaded
        if (!powerBiConfigService.isConfigLoaded()) {
          await powerBiConfigService.ensureConfig();
        }

        const url = getPowerBiUrl(this.currentReport, this.selectedPage);
        if (url) {
          window.open(url, '_blank');
        }
      }
    },

    openPowerBiLogin() {
      window.open('https://app.powerbi.com', '_blank');
      this.dismissAuthNotice();
    },

    dismissAuthNotice() {
      this.showAuthNotice = false;
      localStorage.setItem(AUTH_NOTICE_DISMISSED_KEY, 'true');
    },

    dismissSetupDialog() {
      this.showSetupDialog = false;
      if (this.dontShowSetupAgain) {
        localStorage.setItem(SETUP_DISMISSED_KEY, 'true');
      }
    },

    async loadRefreshStatus() {
      try {
        const status = await powerBiConfigService.getRefreshStatus();
        this.canRefreshDataset = status.canRefresh;
        this.cooldownRemainingMinutes = status.cooldownRemainingMinutes;
        this.lastDatasetRefreshTime = status.lastRefreshTime;
      } catch (error) {
        // Silently fail - refresh status is not critical
        console.warn('Failed to load refresh status:', error);
      }
    },

    async refreshDataset() {
      if (this.datasetRefreshing || !this.canRefreshDataset) return;

      this.datasetRefreshing = true;
      try {
        const result = await powerBiConfigService.refreshDataset();
        const message = result.message || 'Dataset refresh triggered successfully';
        (this as any).$toast?.success(message);

        // Update refresh status
        await this.loadRefreshStatus();
      } catch (error: any) {
        const status = error?.response?.status;
        const data = error?.response?.data;
        let errorMessage: string;

        if (status === 409) {
          // Refresh already in progress
          const triggeredBy = data?.triggeredByUserId;
          const currentUserId = this.$store.getters.userInfo?.id;
          const minutesAgo = data?.minutesAgo || 0;

          if (triggeredBy === currentUserId) {
            errorMessage = `You already triggered a refresh ${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago. Please wait for it to complete.`;
          } else {
            errorMessage = data?.message || 'A dataset refresh is already in progress';
          }
          this.canRefreshDataset = false;
        } else if (status === 429) {
          // Rate limit or cooldown
          errorMessage = data?.message || 'Please wait before refreshing again';
          this.cooldownRemainingMinutes = data?.remainingMinutes || data?.retryAfterMinutes;
          this.canRefreshDataset = false;
        } else if (status === 403) {
          errorMessage = 'You do not have permission to refresh datasets';
        } else {
          errorMessage = data?.message || 'Failed to refresh dataset';
        }
        (this as any).$toast?.error(errorMessage);
      } finally {
        this.datasetRefreshing = false;
      }
    },

    /**
     * Sets up SignalR connection to receive real-time refresh notifications.
     */
    setupSignalRConnection() {
      try {
        const connection = new signalR.HubConnectionBuilder()
          .withUrl('/updateHub')
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        connection.on('PowerBiRefreshCompleted', this.handleRefreshCompleted);

        connection.start().catch((err) => {
          console.warn('SignalR connection failed:', err);
        });

        this.signalRConnection = connection;
      } catch (error) {
        console.warn('Failed to setup SignalR connection:', error);
      }
    },

    /**
     * Handles SignalR notification when a Power BI refresh completes.
     */
    handleRefreshCompleted(notification: PowerBiRefreshNotification) {
      console.info('Power BI refresh completed:', notification);

      this.lastRefreshNotification = notification;

      // Update the displayed refresh time immediately from the notification
      if (notification.completedAt) {
        this.lastDatasetRefreshTime = notification.completedAt;
      }

      // Update refresh status to re-enable the button
      this.loadRefreshStatus();

      // Show dialog to the user
      this.showRefreshCompletedDialog = true;
    },

    /**
     * Dismisses the refresh completed dialog without reloading.
     */
    dismissRefreshCompletedDialog() {
      this.showRefreshCompletedDialog = false;
      this.lastRefreshNotification = null;
    },

    /**
     * Reloads the current report after the refresh completed dialog.
     */
    async reloadReportAfterRefresh() {
      this.showRefreshCompletedDialog = false;
      this.lastRefreshNotification = null;

      // Reload the current report if one is selected
      if (this.selectedPage) {
        await this.refreshReport();
      }
    },

    /**
     * Formats duration in seconds to a human-readable string.
     */
    formatDuration(seconds: number): string {
      if (seconds < 60) {
        return `${seconds} second${seconds !== 1 ? 's' : ''}`;
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (remainingSeconds === 0) {
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
      return `${minutes}m ${remainingSeconds}s`;
    },
  },
});
